/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  code: '',
  type_name: '',
  date_record: '',
  remark: '',
  user_name: '',
  status: 'edit',
};

function ExportRequestView({ request_id }) {
  const mod = 'rp_warehouse_sale';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const open = () => {
    setLoading(true);
    setAttr(initAttr);

    FetchData('OPTIONS', `/${mod}/request`, app.token, {
      id: request_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setLoading(false);

        setAttr(result.data);
        setList(result.list);
        setModalOpen(true);
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.product_name} src={item.image} variant='rounded' />;
  };

  const renderInfo = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          #{attr.code}
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ผู้ขอโอน'
              value={attr.user_name}
              variant='standard'
              InputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='วันที่โอน'
              value={Func.dateDisplay(attr.date_record)}
              variant='standard'
              InputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='หมายเหตุ'
              value={attr.remark}
              variant='standard'
              multiline
              InputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const statusDisplay = (item) => {
    return (
      <Alert
        icon={<Icon>{item.status_icon}</Icon>}
        severity={item.status_color}
        sx={{
          fontSize: '14px',
          padding: '0 10px',
          fontWeight: 400,
          borderRadius: 5,
          whiteSpace: 'nowrap',
        }}
      >
        {item.status_name}
      </Alert>
    );
  };

  const renderList = () => {
    return (
      <MDBox sx={{ border: '1px solid #e2e2e2', borderRadius: 1 }}>
        <MDBox p={2}>
          <MDTypography variant='h5'>{t(`element.order`)}</MDTypography>
        </MDBox>
        <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center'>No.</TableCell>
                <TableCell align='center'>รูป</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>สินค้า</TableCell>
                <TableCell align='right'>จำนวน</TableCell>
                <TableCell>หน่วย</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row, i) => (
                <TableRow key={row.id}>
                  <TableCell align='center' width='100'>
                    {i + 1}
                  </TableCell>
                  <TableCell width='40'>{imageDisplay(row)}</TableCell>
                  <TableCell align='left'>{row.product_sku}</TableCell>
                  <TableCell align='left'>{row.product_name}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.quantity)}</TableCell>
                  <TableCell align='left'>{row.unit_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const btnOpen = () => {
    if (request_id <= 0) return null;
    return (
      <MDButton variant='outlined' tabIndex={-1} color='info' size='small' onClick={open}>
        ใบขอโอน
      </MDButton>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          ใบขอโอน
          <IconButton
            aria-label='close'
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={7}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {statusDisplay(attr)}
              </Grid>
              <Grid item xs={12} xl={3}>
                {renderInfo()}
              </Grid>
              <Grid item xs={12} xl={9}>
                {renderList()}
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      {btnOpen()}
      {renderOpen()}

      {renderLoading()}
    </div>
  );
}

ExportRequestView.propTypes = {
  request_id: PropTypes.number.isRequired,
};

export default ExportRequestView;
